import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

interface KanbanColumn {
  id: string;
  label: string;
  color: string;
  wipLimit?: number;
}

/**
 * Command Handler for Kanban Board components.
 *
 * Handles commands for:
 * - Column management (add/remove columns)
 * - WIP limits
 * - Color coding by field
 * - Card layout switching (compact/standard/detailed)
 * - Column field configuration
 * - Show/hide toggles (column counts, drag)
 */
export class KanbanBoardCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // 1. Add column: "add column Review" or "add a column called 'QA'"
    this.commandPatterns.set(
      /add (?:a )?column (?:named |called )?["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const label = match[1].trim();
        const id = label.toLowerCase().replace(/\s+/g, '-');
        const existingColumns = (component.properties?.columns || []) as KanbanColumn[];
        const newColumn: KanbanColumn = { id, label, color: '#6B7280' };
        return { properties: { columns: [...existingColumns, newColumn] } };
      }
    );

    // 2. Remove column: "remove column Done" or "remove the column called 'QA'"
    this.commandPatterns.set(
      /(?:remove|delete) (?:the )?column (?:named |called )?["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const label = match[1].trim().toLowerCase();
        const existingColumns = (component.properties?.columns || []) as KanbanColumn[];
        const filtered = existingColumns.filter(
          (c) => c.label.toLowerCase() !== label && c.id.toLowerCase() !== label
        );
        if (filtered.length === existingColumns.length) return null;
        return { properties: { columns: filtered } };
      }
    );

    // 3. Set WIP limit: "set wip limit 5 on In Progress" or "set limit to 3 on Done"
    this.commandPatterns.set(
      /set (?:wip |work[\s-]+in[\s-]+progress )?limit (?:to )?(\d+)(?: (?:on|for) (?:the )?["']?(.+?)["']?)?/i,
      (match, component) => {
        const limit = parseInt(match[1], 10);
        const columnName = match[2]?.trim();
        const existingColumns = (component.properties?.columns || []) as KanbanColumn[];

        if (columnName) {
          const updated = existingColumns.map((c) => {
            if (c.label.toLowerCase() === columnName.toLowerCase() || c.id.toLowerCase() === columnName.toLowerCase()) {
              return { ...c, wipLimit: limit };
            }
            return c;
          });
          return { properties: { columns: updated } };
        }
        // Apply to all columns
        const updated = existingColumns.map((c) => ({ ...c, wipLimit: limit }));
        return { properties: { columns: updated } };
      }
    );

    // 4. Color code by field: "color code by priority" or "color code cards by assignee"
    this.commandPatterns.set(
      /color ?code (?:cards )?by (?:the )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { colorField: match[1].trim().toLowerCase() } })
    );

    // 5. Card layout: "set card layout to compact" or "switch to detailed layout"
    this.commandPatterns.set(
      /(?:set |switch (?:to )?|change )?card ?layout (?:to )?(compact|standard|detailed)/i,
      (match) => ({ properties: { cardLayout: match[1].toLowerCase() } })
    );

    // 5b. Alternative: "switch to compact" or "use detailed layout"
    this.commandPatterns.set(
      /(?:switch to|use) (compact|standard|detailed)(?: layout)?/i,
      (match) => ({ properties: { cardLayout: match[1].toLowerCase() } })
    );

    // 6. Set column field: "set column field to status" or "group by priority"
    this.commandPatterns.set(
      /(?:set column ?field|group (?:cards )?by) (?:to )?["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const field = match[1].trim().toLowerCase();
        const existing = component.properties?.fieldMapping || { title: 'title', column: 'status' };
        return { properties: { fieldMapping: { ...existing, column: field } } };
      }
    );

    // 7. Show/hide column counts
    this.commandPatterns.set(
      /(show|hide|enable|disable) column ?counts?/i,
      (match) => {
        const show = /^(show|enable)$/i.test(match[1]);
        return { properties: { showColumnCounts: show } };
      }
    );

    // 8. Enable/disable drag
    this.commandPatterns.set(
      /(enable|disable) drag(?:ging)?(?:\s*(?:and\s*)?drop)?/i,
      (match) => {
        const enabled = /^enable$/i.test(match[1]);
        return { properties: { dragEnabled: enabled } };
      }
    );

    // 9. Rename column: "rename column 'To Do' to 'Backlog'"
    this.commandPatterns.set(
      /rename column ["']?(.+?)["']? (?:to|as) ["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const oldName = match[1].trim().toLowerCase();
        const newLabel = match[2].trim();
        const newId = newLabel.toLowerCase().replace(/\s+/g, '-');
        const existingColumns = (component.properties?.columns || []) as KanbanColumn[];
        const updated = existingColumns.map((c) => {
          if (c.label.toLowerCase() === oldName || c.id.toLowerCase() === oldName) {
            return { ...c, id: newId, label: newLabel };
          }
          return c;
        });
        return { properties: { columns: updated } };
      }
    );

    // 10. Set column color: "set column 'Done' color to green"
    this.commandPatterns.set(
      /set column ["']?(.+?)["']? color (?:to )?(.+)/i,
      (match, component) => {
        const columnName = match[1].trim().toLowerCase();
        const color = this.parseColorValue(match[2].trim());
        if (!color) return null;
        const existingColumns = (component.properties?.columns || []) as KanbanColumn[];
        const updated = existingColumns.map((c) => {
          if (c.label.toLowerCase() === columnName || c.id.toLowerCase() === columnName) {
            return { ...c, color };
          }
          return c;
        });
        return { properties: { columns: updated } };
      }
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.KANBAN_BOARD) {
      return false;
    }

    const keywords = [
      "column", "columns", "wip", "limit",
      "card layout", "compact", "standard", "detailed",
      "color code", "group by", "column field",
      "column count", "drag", "rename column",
    ];
    const lowerCommand = command.toLowerCase().trim();
    return keywords.some((keyword) => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);

    if (changes) {
      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component),
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    return {
      success: false,
      error: "Could not understand the kanban board command",
      suggestions: await this.getSuggestions(component),
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'add column Review',
      'remove column Done',
      'set wip limit 5 on In Progress',
      'color code by priority',
      'set card layout to compact',
      'set card layout to detailed',
      'group by assignee',
      'show column counts',
      'enable drag and drop',
      'rename column To Do to Backlog',
      'set column Done color to green',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const props = component.properties || {};
    const suggestions: string[] = [];

    suggestions.push('add column Review');

    if (props.cardLayout === 'standard' || !props.cardLayout) {
      suggestions.push('set card layout to compact');
      suggestions.push('set card layout to detailed');
    } else if (props.cardLayout === 'compact') {
      suggestions.push('set card layout to standard');
      suggestions.push('set card layout to detailed');
    } else {
      suggestions.push('set card layout to compact');
      suggestions.push('set card layout to standard');
    }

    suggestions.push('color code by priority');
    suggestions.push('set wip limit 5 on In Progress');

    if (props.showColumnCounts === false) {
      suggestions.push('show column counts');
    } else {
      suggestions.push('hide column counts');
    }

    if (props.dragEnabled === false) {
      suggestions.push('enable drag and drop');
    } else {
      suggestions.push('disable drag and drop');
    }

    return suggestions;
  }
}

export const kanbanBoardCommandHandler = new KanbanBoardCommandHandler();
